import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO + social-share meta. Sets:
 *   - <title>            (browser tab + most modern share crawlers that run JS)
 *   - <meta description>
 *   - Open Graph + Twitter card meta tags
 *
 * For Apple iMessage / Discord / Slack / X (which all execute JS for previews on
 * recent versions), this gives proper per-page link previews. Pure non-JS
 * crawlers (older WhatsApp, Facebook scraper) will fall back to the static
 * defaults baked into /public/index.html.
 *
 * Usage:
 *   <Seo title="Distribution" description="…" image="/assets/og-cover.jpg" />
 */
export default function Seo({
  title,
  description,
  image,
  url,
}) {
  const fullTitle = title ? `${title} | Pulse Media` : "Pulse Media | Home";
  const desc =
    description ||
    "Music. Film. Technology. — Pulse Media is a four-branch record label and media company at the intersection of entertainment and technology.";

  // Resolve relative image paths to absolute URLs so social crawlers can fetch them.
  const resolveImage = (img) => {
    if (!img) return "https://pulsemedia.vip/assets/og-cover.jpg";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    if (img.startsWith("/api/")) {
      // Backend-served upload — prepend the public domain.
      const base = process.env.REACT_APP_BACKEND_URL || "";
      return base ? `${base}${img}` : `https://pulsemedia.vip${img}`;
    }
    if (img.startsWith("/")) return `https://pulsemedia.vip${img}`;
    return img;
  };

  const ogImage = resolveImage(image);
  const ogUrl = url || (typeof window !== "undefined" ? window.location.href : "https://pulsemedia.vip");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content="Pulse Media" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
